import * as PleasedApi from '@/common/api/api.pleased';
import type { ActiveOpenChat } from '@/common/api/models.pleased';
import { FirebaseStore } from '@/pleased-sdk/PleasedChat/FirebaseStore';
import { serverTimestamp, update } from 'firebase/database';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';

export type EndUserEndChatPresenceParams = {
  chatId: number;
  firebaseToken: string;
  customerId: number;
};

export async function writeEndUserEndChatPresence({
  chatId,
  firebaseToken,
  customerId,
}: EndUserEndChatPresenceParams): Promise<void> {
  const token = firebaseToken?.trim();
  if (!token) {
    return;
  }

  const store = FirebaseStore.get(chatId, token);

  try {
    await update(store.presence, {
      status: 'offline',
      chatId,
      customerId,
      lastStatusUpdateTime: serverTimestamp(),
      reason: 'end-chat-local',
      clickedEndChat: true,
      clickedEndChatAt: serverTimestamp(),
    });
  } finally {
    await FirebaseStore.destroy(store);
  }
}

/**
 * Ends an open/resumable chat from the widget: backend notification + Firebase presence for agents.
 */
export async function endResumableChatWithPresence(chat: Pick<ActiveOpenChat, 'id' | 'firebaseToken' | 'customerId'>) {
  await PleasedApi.reportChatEnded.once({ chatId: chat.id });

  if (chat.firebaseToken?.trim() && chat.customerId != null) {
    await writeEndUserEndChatPresence({
      chatId: chat.id,
      firebaseToken: chat.firebaseToken,
      customerId: chat.customerId,
    }).catch((err) => {
      logger.error('[RESUMABLE_CHAT_PRESENCE] Failed to write end-user presence', {
        chatId: chat.id,
        error: parseErrorMessage(err),
      });
    });
  } else {
    logger.warn('[RESUMABLE_CHAT_PRESENCE] Skipped end-user presence: missing firebase data', {
      chatId: chat.id,
    });
  }
}
